import type React from "react";
import { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Paper,
} from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { motion, AnimatePresence } from "framer-motion";
import type { Role, RolePermission } from "./types/permissions";
import { roles as initialRoles } from "./mockData/data";
import AddRoleModal from "./AddRole";
import PermissionsManagement from "./PermissionsManagement";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";

const RoleCard: React.FC<{
  role: Role;
  onManagePermissions: (role: Role) => void;
  index: number;
}> = ({ role, onManagePermissions, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay: index * 0.1 }}
    whileHover={{ y: -4 }}
  >
    <Card sx={{ boxShadow: 0, height: "100%", transition: "all 0.3s ease" }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" mb={1}>
          {role.title}
        </Typography>
        <Typography variant="body2" mb={2} color="text.secondary">
          {role.description}
        </Typography>
        <Box display="flex" alignItems="center" mb={2}>
          <PeopleAltOutlinedIcon
            fontSize="small"
            sx={{ mr: 0.5, color: "primary.main" }}
          />
          <Typography variant="body2" fontWeight="medium">
            {role.managers} User{role.managers > 1 ? "s" : ""}
          </Typography>
        </Box>

        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onManagePermissions(role)}
            sx={{ flexGrow: 1 }}
          >
            Manage Permissions
          </Button>
        </Box>
      </CardContent>
    </Card>
  </motion.div>
);

const RolesPermissions: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>(initialRoles);
  const [addRoleModalOpen, setAddRoleModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [showPermissionsView, setShowPermissionsView] = useState(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const handleAddRole = (newRoleData: {
    title: string;
    description: string;
    permissionLevel: string;
  }) => {
    const newRole: Role = {
      id: Date.now().toString(),
      title: newRoleData.title,
      description: newRoleData.description,
      managers: 0,
      addLabel: `Add New ${newRoleData.title}`,
      permissionLevel: newRoleData.permissionLevel,
      permissions: [],
    };
    setRoles((prev) => [...prev, newRole]);
  };

  const handleManagePermissions = (role: Role) => {
    setSelectedRole(role);
    setShowPermissionsView(true);
  };

  const handleBackToRoles = () => {
    setShowPermissionsView(false);
    setSelectedRole(null);
  };

  const handleUpdatePermissions = (
    roleId: string,
    permissions: RolePermission[]
  ) => {
    setRoles((prev) =>
      prev.map((role) => (role.id === roleId ? { ...role, permissions } : role))
    );
  };

  return (
    <Box>
      <AnimatePresence mode="wait">
        {!showPermissionsView ? (
          <motion.div
            key="roles-list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Paper
              sx={{
                boxShadow: 0,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                p: 3,

                color: reduxTheme.primary_colors[100],
              }}
            >
              <Box>
                <Typography variant="h4" fontWeight="bold" mb={1}>
                  Roles & Permissions
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Review your members roles and allocate permissions
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                onClick={() => setAddRoleModalOpen(true)}
                sx={{
                  backgroundColor: reduxTheme.primary_colors[400],
                }}
              >
                Add New Role
              </Button>
            </Paper>

            <Grid container spacing={3}>
              {roles.map((role, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={role.id}>
                  <RoleCard
                    role={role}
                    onManagePermissions={handleManagePermissions}
                    index={idx}
                  />
                </Grid>
              ))}
            </Grid>
          </motion.div>
        ) : (
          selectedRole && (
            <motion.div
              key="permissions-management"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <PermissionsManagement
                role={selectedRole}
                onBack={handleBackToRoles}
                onUpdatePermissions={handleUpdatePermissions}
              />
            </motion.div>
          )
        )}
      </AnimatePresence>

      <AddRoleModal
        open={addRoleModalOpen}
        onClose={() => setAddRoleModalOpen(false)}
        onSave={handleAddRole}
      />
    </Box>
  );
};

export default RolesPermissions;
